import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus} from 'platform/components';
import {Show, useDevice, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useContext} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  MotWarningPeriodResponseBody,
  useGetVehicleMotWarningsQuery,
  useGetVehicleStockOverviewQuery,
  useLazyCreateDataQueryQuery,
  VehicleMotWarningResponseBody,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, suffixTestId, TestIdProps, useLatest, useNavigate} from 'shared/web';

import {convertToSearchParams} from 'features/eag-grid';

import {useRedirectFromOpportunities} from '../../hooks/useRedirectFromOpportunities/useRedirectFromOpportunities';
import {getGridCode} from '../../hooks/useRedirectFromOpportunities/utils/getGridCode';
import {DashboardTileSize} from '../../types/DashboardTileSize';
import {getVehicleMotDataQuery, VehicleMotCategory} from '../../utils/getVehicleMotDataQuery';
import {DashboardAdaptiveDensityContext} from '../DashboardAdaptiveDensityContext';
import {AggregateMotWarnings} from './AggregateMotWarnings';
import {WarehouseMetricsVariant} from './types/WarehouseMetricsVariant';
import {getColumns} from './utils/getColumns';
import {VehicleMotAllGood} from './VehicleMotAllGood';
import {VehicleMotSection} from './VehicleMotSection';
import {VehicleWarehouseAdaptiveMot} from './VehicleWarehouseAdaptiveMot';
import {WarehouseMetrics} from './WarehouseMetrics';

interface VehicleWarehouseProps extends TestIdProps {
  branchId: string | undefined;
  isActiveBranch: boolean;
  canReadVehicle: boolean;
  size: DashboardTileSize;
}

// Mirrors `VehicleMotWarningsQuery::MAX_LIMIT`: the API rejects a larger limit with a 400.
const MAX_PREVIEW_ROWS = 6;

// The wide tile shows the metrics as one-line grey tiles (4px padding + a 24px `base` line = 32px,
// so 8 steps), which keeps the loading placeholder from reserving the taller `Display` strip.
const COMPACT_METRICS_MIN_HEIGHT = 8;
const DEFAULT_METRICS_MIN_HEIGHT = 20;

// The metric tiles keep an even 16px of breathing room above (from the card header) and below.
// 224px of body minus the 32px metric tiles and this 16px gap leaves 176px per MOT column, which is
// the 24px header (its `base` title line) + 4px gap + five 21px link-line rows separated by
// 4px + 1px separator + 4px (141px), so the fifth row lands with 7px of slack.
const BODY_SPACING = 4;

export function VehicleWarehouse(props: VehicleWarehouseProps) {
  const navigate = useNavigate();
  const device = useDevice();
  const adaptiveDensityCoordinator = useContext(DashboardAdaptiveDensityContext);
  const currentUser = useGetCurrentUserInfoQuery();
  const [createDataQuery, createDataQueryState] = useLazyCreateDataQueryQuery();
  const [handleRedirectFromOpportunity, isRedirectingFromOpportunity] =
    useRedirectFromOpportunities({
      branchId: props.branchId,
      ownerSegment: null,
      typeSegment: null,
    });

  const allBranchIds = currentUser.data?.branches?.map((branch) => branch.id) ?? [];
  const activeBranchIds = isNotNil(props.branchId) ? [props.branchId] : [];
  const branchIds = props.isActiveBranch ? activeBranchIds : allBranchIds;
  const isScopeInitializing =
    currentUser.isLoading && (!props.isActiveBranch || isNil(props.branchId));
  const isScopeReady = branchIds.length > 0;
  const isScopeError = currentUser.isError && !isScopeReady;
  const dashboardQueryArg = isScopeReady
    ? {branchId: props.isActiveBranch ? props.branchId : undefined}
    : skipToken;
  const effectiveSize = device === 'mobile' ? 'half' : props.size;
  const isWide = effectiveSize === 'wide';
  const motRowLayout = isWide ? 'inline' : 'stacked';
  const metricsVariant: WarehouseMetricsVariant = isWide ? 'compact' : 'default';
  const previewQueryArg = isScopeReady
    ? {
        branchId: props.isActiveBranch ? props.branchId : undefined,
        limit: MAX_PREVIEW_ROWS,
      }
    : skipToken;

  const overview = useGetVehicleStockOverviewQuery(dashboardQueryArg, {
    refetchOnMountOrArgChange: true,
  });

  const overviewData = overview.currentData;
  const numberOfCarsWhichMotExpirating = overviewData?.motExpirationWarning;
  const numberOfCarsWhichMotExpired = overviewData?.motExpiredWarning;
  // Unknown counts are not a clean bill of health, so the success state waits for the overview.
  const isAllMotValid = numberOfCarsWhichMotExpired === 0 && numberOfCarsWhichMotExpirating === 0;
  // The preview loads in parallel with the overview; an all-valid fleet just does not render it.
  const shouldLoadPreview =
    props.canReadVehicle &&
    device !== 'mobile' &&
    isScopeReady &&
    (effectiveSize !== 'half' || adaptiveDensityCoordinator !== null);

  const preview = useGetVehicleMotWarningsQuery(shouldLoadPreview ? previewQueryArg : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const scopeKey = `${props.isActiveBranch.toString()}:${props.branchId ?? 'all'}:${branchIds.join(',')}`;
  const currentScopeKey = useLatest(scopeKey);

  const redirectGridCode = getGridCode('warehouseTotal');
  const previewData = preview.currentData;
  const warningPeriod = previewData?.period ?? overviewData?.motWarningPeriod;
  const columns = getColumns(overviewData);

  const openWarningCategory = (
    category: VehicleMotCategory,
    period: MotWarningPeriodResponseBody | undefined
  ) => {
    if (!isScopeReady || isNil(period)) {
      return;
    }

    const requestedScopeKey = scopeKey;

    createDataQuery({
      code: redirectGridCode,
      createDataQueryRequestBody: getVehicleMotDataQuery({category, period, branchIds}),
    })
      .unwrap()
      .then((data) => {
        if (currentScopeKey.current !== requestedScopeKey) {
          return;
        }

        const queryParams = convertToSearchParams({
          dataGrids: {
            gridCode: redirectGridCode,
            queryId: data.dataQueryId,
            rowIndex: '1',
          },
        });
        navigate(composePath(vehiclesRoutes.home, {queryParams}));
      })
      .catch(handleApiError);
  };

  const renderAggregateWarnings = (isProbe = false) => (
    <AggregateMotWarnings
      canReadVehicle={props.canReadVehicle}
      isViewLoading={createDataQueryState.isLoading}
      expiredCount={numberOfCarsWhichMotExpired ?? 0}
      expiringCount={numberOfCarsWhichMotExpirating ?? 0}
      isAllMotValid={isAllMotValid}
      onViewExpired={() => openWarningCategory('expired', overviewData?.motWarningPeriod)}
      onViewExpiring={() => openWarningCategory('expiring', overviewData?.motWarningPeriod)}
      data-testid={isProbe ? undefined : props['data-testid']}
    />
  );

  const renderMotSection = (
    category: VehicleMotCategory,
    count: number | undefined,
    rows: VehicleMotWarningResponseBody[],
    rowLimit: number,
    isProbe = false
  ) => (
    <VehicleMotSection
      category={category}
      count={count}
      rows={rows}
      rowLimit={rowLimit}
      layout={motRowLayout}
      isAllBranches={!props.isActiveBranch}
      isViewLoading={createDataQueryState.isLoading}
      canViewAll={props.canReadVehicle && isScopeReady && isNotNil(warningPeriod)}
      isPreviewLoading={preview.isLoading || preview.isFetching || preview.isUninitialized}
      isPreviewError={preview.isError}
      isProbe={isProbe}
      onRetry={preview.refetch}
      onViewAll={(selectedCategory) => openWarningCategory(selectedCategory, warningPeriod)}
      data-testid={isProbe ? undefined : suffixTestId(`mot-${category}`, props)}
    />
  );

  const renderMetrics = () => (
    <DataStatus
      minHeight={isWide ? COMPACT_METRICS_MIN_HEIGHT : DEFAULT_METRICS_MIN_HEIGHT}
      isLoading={isScopeInitializing || overview.isLoading}
      isError={overview.isError || isScopeError}
      isEmpty={!isScopeInitializing && !isScopeReady}
    >
      <VStack spacing={4}>
        <WarehouseMetrics
          columns={columns}
          isRedirecting={isRedirectingFromOpportunity}
          variant={metricsVariant}
          onOpenMetric={handleRedirectFromOpportunity}
        />
        <Show when={!props.canReadVehicle || device === 'mobile'}>{renderAggregateWarnings()}</Show>
      </VStack>
    </DataStatus>
  );

  const renderMotContent = () =>
    match({isAllMotValid, size: effectiveSize})
      // Only the sizes that would list the two sections have the room for a centred status.
      .with({isAllMotValid: true, size: Pattern.union('tall', 'wide')}, () => (
        <VehicleMotAllGood data-testid={props['data-testid']} />
      ))
      // The remaining sizes keep their aggregate slot, where the success shows as an inline alert.
      .with({isAllMotValid: true}, () => renderAggregateWarnings())
      .otherwise(() => (
        <VehicleWarehouseAdaptiveMot
          key={scopeKey}
          size={effectiveSize}
          expiredCount={numberOfCarsWhichMotExpired}
          expiringCount={numberOfCarsWhichMotExpirating}
          expiredRows={previewData?.expiredVehicles ?? []}
          expiringRows={previewData?.expiringVehicles ?? []}
          isDetailReady={isNotNil(previewData) || preview.isError}
          renderAggregate={renderAggregateWarnings}
          renderSection={renderMotSection}
        />
      ));

  const renderAdaptiveMot = () => (
    <Show when={props.canReadVehicle && device !== 'mobile' && isScopeReady}>
      {renderMotContent()}
    </Show>
  );

  return (
    <Card title={i18n.t('page.homepage.labels.vehicleWarehouse')} hasSeparator={false} isFullHeight>
      <VStack spacing={BODY_SPACING} height="100%">
        {renderMetrics()}
        {renderAdaptiveMot()}
      </VStack>
    </Card>
  );
}
